import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/src/components/ui/card';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { Skeleton } from '@repo/ui/src/components/ui/skeleton';
import type { InvoiceReviewTarget } from '@repo/shared-types';
import { formatMonthKey } from '../../../lib/sessionTime';
import { useBulkCreateProgressLinks } from '../../../hooks/useProgress';
import { useSubscription } from '../../../hooks/useSubscription';
import { useUpgradeDialogStore } from '../../../stores/upgradeDialogStore';

/** Invoice detail's "Nhận xét tháng {{month}}" card (THI-189, spec §10.4a).
 *  Shown for EVERY status on a chốt-sổ invoice (it is a PATH to the reviews,
 *  not a pre-send checklist) — the caller decides visibility by checking
 *  `reviewTargets.length > 0` before rendering this at all. */
export function ReviewTargetsCard({ targets }: { targets: InvoiceReviewTarget[] }) {
  const { t } = useTranslation('progress');
  const bulk = useBulkCreateProgressLinks();
  // Same plan-gate rule as ProgressLinkBlock/useProgressLink(Summary):
  // `allowsProgressLink` decides whether the bulk-create button is even
  // allowed to POST. A FREE org's students can never carry a progressToken
  // (creation is gated server-side too), so `missingLinkIds` below would be
  // non-empty on effectively every chốt-sổ invoice there — rendering the
  // prompt is fine (it's informational), but the button must not fire the
  // request; it opens the upgrade dialog instead, same as the "Bán thêm"
  // lock affordance on InvoicesPage.
  //
  // `allowsProgressLink` has three readings, not two, exactly like
  // ProgressLinkBlock documents: while `subscription` is still pending (a
  // cold load), reading `subscription?.plan.allowsProgressLink === true` as
  // `false` would flash the LOCKED "Xem gói Pro" CTA at every org — Pro and
  // Center included — for the split second before the subscription query
  // resolves, and a fast click during that window would pop the upgrade
  // dialog for a feature the org actually owns. A subscription fetch error
  // carries no plan info either, so it must not read as "locked" any more
  // than "pending" does. Both collapse into `planUnknown` below: the action
  // renders as a neutral skeleton, never the locked CTA, until the plan is
  // actually known one way or the other.
  const { data: subscription, isPending: subscriptionPending, isError: subscriptionError } = useSubscription();
  if (targets.length === 0) return null;

  const month = targets[0]!.month;
  // A student can appear once per SUBJECT in `targets` — dedupe before
  // sending, so the bulk endpoint's `studentIds` never carries a repeat.
  const missingLinkIds = [...new Set(targets.filter((r) => !r.hasProgressLink).map((r) => r.studentId))];
  const planUnknown = subscriptionPending || subscriptionError;
  const planAllows = subscription?.plan.allowsProgressLink === true;

  const createLinks = () => {
    if (!planAllows) {
      useUpgradeDialogStore.getState().open('progressLink');
      return;
    }
    bulk.mutate(missingLinkIds, {
      onSuccess: (d) => toast.success(t('link.bulkCreatedToast', { count: d.created })),
    });
  };

  return (
    <Card className="shadow-none">
      <CardHeader>
        {/* CD review item 4: the title spells out that the month being
         *  reviewed is the one just finished, not the current one — same
         *  line D5's slip footer uses (`invoices` ns, added by Task 3). */}
        <CardTitle className="text-sm font-semibold">
          {t('reviewTargets.title', { month: formatMonthKey(month), ns: 'invoices' })}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {targets.map((r) => (
          <Link
            key={`${r.studentId}-${r.subjectId}`}
            to={`/subjects/${r.subjectId}/monthly-reviews?month=${r.month}&student=${r.studentId}`}
            className="flex min-h-[44px] items-center justify-between gap-3 rounded-md border border-surface-200 px-3 text-left hover:bg-surface-50"
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13.5px] font-medium text-surface-800">{r.studentName}</span>
              <span className="block text-[12px] text-surface-500">{r.subjectName}</span>
            </span>
            <span className="flex shrink-0 items-center gap-1">
              <Badge variant={r.hasReview ? 'success' : 'muted'}>
                {r.hasReview ? t('review.written') : t('review.notWritten')}
              </Badge>
              {/* CD review item 4: an unwritten row keeps a bare `muted`
               *  badge, which reads as inert though the whole row IS a
               *  link — a trailing chevron signals it's tappable. A written
               *  row (nothing left to do here) drops the chevron. */}
              {!r.hasReview && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-surface-400" strokeWidth={2} />}
            </span>
          </Link>
        ))}

        {missingLinkIds.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-md bg-surface-50 px-3 py-2">
            <span className="text-[12.5px] text-surface-600">{t('link.bulkPrompt', { count: missingLinkIds.length })}</span>
            {planUnknown ? (
              <Skeleton className="h-4 w-16" />
            ) : (
              <button
                type="button"
                onClick={createLinks}
                disabled={bulk.isPending}
                className="text-[12.5px] font-medium text-brand-600 hover:underline disabled:opacity-50"
              >
                {planAllows ? t('link.bulkPromptAction') : t('link.proCta')}
              </button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
